import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';
import { useRelayEnvironment } from 'react-relay/hooks';

import { graphql } from 'babel-plugin-relay/macro';
import { fetchQuery } from 'relay-runtime';

import { usePrevious } from '@lib/hooks';
import useDebounce from '@lib/hooks/useDebounce';

import { TextFieldSearch, TextFieldSearchProps } from 'components/v2';
import { isValidPhoneNumber } from 'utils/validation';

import {
  QueryTextFieldSearchPatientQuery,
  QueryTextFieldSearchPatientQueryResponse,
} from './__generated__/QueryTextFieldSearchPatientQuery.graphql';

const query = graphql`
  query QueryTextFieldSearchPatientQuery($query: String!) {
    patients: patientSearchByText(query: $query) {
      _id

      # TODO move fragment out of QueryTextFieldSearchPatient once ListChatActivities is done
      ...ListItemPatientChatActivityEmpty_Patient
    }
  }
`;

const messages = defineMessages({
  placeholder: {
    id: 'deprecated.queries.QueryTextFieldSearchPatient.placeholder',
    defaultMessage: 'Search for patient',
  },
});

const parseSearchText = (searchText: string) => {
  const searchTextWithoutWhiteSpaces = searchText.replace(/\s/g, '');
  // I don't know why, but isValidPhoneNumber returns false to valid US numbers, so we need to call it as !isValidPhoneNumber
  return !isValidPhoneNumber(searchTextWithoutWhiteSpaces) ? searchTextWithoutWhiteSpaces : searchText;
};

export type QueryTextFieldSearchPatientPatients = QueryTextFieldSearchPatientQueryResponse['patients'];

export interface QueryTextFieldSearchPatientProps extends Omit<TextFieldSearchProps, 'classes' | 'onChange'> {
  filterBy?: 'name' | 'dateOfBirth' | 'email' | 'phone'[];
  value?: string;
  onChange: (search: string, patients: QueryTextFieldSearchPatientPatients) => void;
}

const QueryTextFieldSearchPatient: React.FC<QueryTextFieldSearchPatientProps> = ({
  filterBy = ['name', 'dateOfBirth', 'email', 'phone'],
  value: valueProp = '',
  onChange,
  ...rest
}) => {
  const environment = useRelayEnvironment();
  const { formatMessage } = useIntl();
  const [value, setValue] = useState(valueProp);
  const [valueDebounced] = useDebounce(value, 1000);
  const preValueDebounced = usePrevious(valueDebounced);

  useEffect(() => {
    setValue(valueProp);
  }, [valueProp, setValue]);

  useEffect(() => {
    if (!valueDebounced || valueDebounced === preValueDebounced || valueDebounced.length < 3) {
      return;
    }
    fetchQuery<QueryTextFieldSearchPatientQuery>(environment, query, {
      query: parseSearchText(valueDebounced),
    }).then((data) => onChange(valueDebounced, data.patients));
  }, [valueDebounced, preValueDebounced, environment, onChange]);

  const handleChange = useCallback(
    (text: string) => {
      if (!text) {
        onChange('', []);
      }
      setValue(text);
    },
    [onChange, setValue],
  );

  return (
    <TextFieldSearch
      placeholder={formatMessage(messages.placeholder)}
      value={value}
      onChange={handleChange}
      inputProps={{
        'data-lh-id': 'search-patient-hub',
      }}
      {...rest}
    />
  );
};

export default QueryTextFieldSearchPatient;
